import { createClient } from '@supabase/supabase-js'
import type { Database } from '../types/database'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://placeholder.supabase.co'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'placeholder-key'

if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
  console.warn(
    '⚠️ Missing Supabase environment variables. Using placeholder values for development.\n' +
    'Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.'
  )
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  global: {
    headers: {
      'X-Client-Info': 'sayit-frontend'
    }
  }
})

// Helper functions for common operations
export const auth = {
  signUp: async (email: string, password: string, metadata?: Record<string, any>) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    })
    return { data, error }
  },

  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    return { data, error }
  },

  signInWithProvider: async (provider: 'google' | 'github' | 'apple') => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/dashboard`
      }
    })
    return { data, error }
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  resetPassword: async (email: string) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })
    return { data, error }
  },

  updatePassword: async (password: string) => {
    const { data, error } = await supabase.auth.updateUser({
      password
    })
    return { data, error }
  },

  updateProfile: async (updates: Record<string, any>) => {
    const { data, error } = await supabase.auth.updateUser({
      data: updates
    })
    return { data, error }
  }
}

export const storage = {
  uploadAudio: async (file: File, path: string) => {
    const { data, error } = await supabase.storage
      .from('audio-recordings')
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false
      })
    return { data, error }
  },

  getAudioUrl: (path: string) => {
    const { data } = supabase.storage
      .from('audio-recordings')
      .getPublicUrl(path)
    return data.publicUrl
  },

  deleteAudio: async (path: string) => {
    const { data, error } = await supabase.storage
      .from('audio-recordings')
      .remove([path])
    return { data, error }
  },

  uploadImage: async (file: File, bucket: string, path: string) => {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: false
      })
    return { data, error }
  },

  getImageUrl: (bucket: string, path: string) => {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path)
    return data.publicUrl
  }
}

export const realtime = {
  subscribeToUserProgress: (userId: string, callback: (payload: any) => void) => {
    return supabase
      .channel('user-progress')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_progress',
          filter: `user_id=eq.${userId}`
        },
        callback
      )
      .subscribe()
  },

  subscribeToLeaderboard: (callback: (payload: any) => void) => {
    return supabase
      .channel('leaderboard')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles'
        },
        callback
      )
      .subscribe()
  },

  unsubscribe: (channel: any) => {
    return supabase.removeChannel(channel)
  }
}

// Error handling helper
export const handleSupabaseError = (error: any): string => {
  if (!error) return 'An unknown error occurred'
  
  // Handle specific error codes
  switch (error.code) {
    case 'invalid_credentials':
      return 'Invalid email or password'
    case 'email_not_confirmed':
      return 'Please check your email and click the confirmation link'
    case 'signup_disabled':
      return 'Sign up is currently disabled'
    case 'email_address_invalid':
      return 'Please enter a valid email address'
    case 'password_too_short':
      return 'Password must be at least 6 characters long'
    case 'user_already_exists':
      return 'An account with this email already exists'
    case 'rate_limit_exceeded':
      return 'Too many requests. Please try again later'
    default:
      return error.message || 'An error occurred'
  }
}

export default supabase